import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { useRequireRole } from "@/lib/use-require-role";
import { PageHeader } from "@/components/page-header";
import { clientForPortalUser } from "@/mocks/saas-data";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { nameField, emailField, optionalText } from "@/lib/validators";

export const Route = createFileRoute("/portal/profile")({
  ssr: false,
  head: () => ({ meta: [{ title: "Portal — Profile" }] }),
  component: Page,
});

const schema = z.object({ name: nameField(100), email: emailField, phone: optionalText(30) });
type Values = z.infer<typeof schema>;

function Page() {
  const user = useRequireRole(["client_portal"]);
  const client = user ? clientForPortalUser(user.id) : null;
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: client?.name ?? "", email: user?.email ?? "", phone: "" },
  });
  if (!user) return null;

  return (
    <div className="mx-auto max-w-2xl px-6 py-8 lg:px-10 lg:py-10">
      <PageHeader title="Your profile" description="Update your contact info." />
      <form
        onSubmit={handleSubmit(async () => { await new Promise((r) => setTimeout(r, 300)); toast.success("Profile saved"); })}
        className="space-y-4 rounded-xl bg-card p-6 ring-1 ring-border"
      >
        <div className="space-y-1.5"><Label>Name</Label><Input {...register("name")} />{errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}</div>
        <div className="space-y-1.5"><Label>Email</Label><Input type="email" {...register("email")} />{errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}</div>
        <div className="space-y-1.5"><Label>Phone</Label><Input {...register("phone")} />{errors.phone && <p className="text-xs text-destructive">{errors.phone.message}</p>}</div>
        <Button type="submit" disabled={isSubmitting}>Save changes</Button>
      </form>
    </div>
  );
}
